import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { FaCrown } from "react-icons/fa";

import { colors } from "@/theme";

/** The mascot's colors, as the pages built around it use them. */
export const MASCOT_PALETTE = {
  sky: "#5BC8FF",
  /** The blue an icon or a name takes on a light page, where the sky blue is too pale. */
  deepInk: "#0B63D6",
  crown: "#FDC445",
  night: "#0A1830",
  deep: "#0B2A66",
  cloud: "#EAF4FF",
  mist: "#CFEBFF",
} as const;

const MASCOT_SRC = "/assets/images/mascot-hero.webp";

/**
 * The panel a page opens on when it has something to tell the player about
 * themselves: their VIP standing, their offers.
 *
 * The one surface on those pages lit like the mascot artwork: cloud white
 * running to pale sky in the light scheme, night navy running to deep blue in
 * the dark one, with the blue light coming up from the corner the mascot
 * stands in.
 */
export const MascotPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: 16,
  border: "1px solid",
  borderColor: alpha(colors.prime, 0.28),
  background: `radial-gradient(60% 120% at 0% 100%, ${alpha(colors.prime, 0.22)} 0%, transparent 70%), linear-gradient(135deg, ${MASCOT_PALETTE.cloud}, ${MASCOT_PALETTE.mist})`,
  boxShadow: `0 10px 30px ${alpha(colors.prime, 0.16)}`,
  ...theme.applyStyles("dark", {
    borderColor: alpha(MASCOT_PALETTE.sky, 0.28),
    background: `radial-gradient(60% 120% at 0% 100%, ${alpha(colors.prime, 0.38)} 0%, transparent 70%), linear-gradient(135deg, ${MASCOT_PALETTE.night}, ${MASCOT_PALETTE.deep})`,
    boxShadow: `0 10px 30px ${alpha("#000000", 0.45)}`,
  }),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 2),
  },
}));

const Medallion = styled(Box)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  width: 112,
  height: 112,
  borderRadius: "50%",
  border: `3px solid ${MASCOT_PALETTE.sky}`,
  boxShadow: `0 0 0 6px ${alpha(MASCOT_PALETTE.sky, 0.18)}, 0 10px 24px ${alpha("#000000", 0.35)}`,
  backgroundColor: MASCOT_PALETTE.night,
  [theme.breakpoints.down("sm")]: { width: 92, height: 92, alignSelf: "center" },
}));

/**
 * The artwork inside the porthole. It is the whole figure on a rectangle, so it
 * is drawn larger than the disc and placed to frame the face.
 */
const Portrait = styled(Box)({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  backgroundImage: `url(${MASCOT_SRC})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "215%",
  backgroundPosition: "53% 12%",
});

const Crown = styled(Box)({
  position: "absolute",
  top: -6,
  right: -6,
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  fontSize: 17,
  color: MASCOT_PALETTE.night,
  backgroundColor: MASCOT_PALETTE.crown,
  boxShadow: `0 4px 10px ${alpha(MASCOT_PALETTE.crown, 0.5)}`,
});

interface MascotBadgeProps {
  /** Pins the crown to the porthole: the player has something to show for it. */
  crowned?: boolean;
}

/** The mascot, in a porthole ringed in sky blue. Decoration, so hidden from readers. */
export const MascotBadge = ({ crowned = false }: MascotBadgeProps) => (
  <Medallion aria-hidden>
    <Portrait />
    {crowned && (
      <Crown>
        <FaCrown />
      </Crown>
    )}
  </Medallion>
);

/**
 * A small card with an emblem beside its text: a step of "how it works", a
 * figure the player has earned.
 */
export const EmblemCard = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  borderRadius: 14,
  border: "1px solid",
  borderColor: theme.palette.divider,
  backgroundColor: alpha(colors.prime, 0.05),
  ...theme.applyStyles("dark", { backgroundColor: alpha(colors.prime, 0.09) }),
}));

/** The emblem: one of the things the mascot holds, on its night navy. */
export const Emblem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: string }>(({ tone }) => ({
  flexShrink: 0,
  width: 46,
  height: 46,
  display: "grid",
  placeItems: "center",
  borderRadius: 12,
  fontSize: 24,
  fontWeight: 800,
  color: tone,
  backgroundColor: MASCOT_PALETTE.night,
  boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.5)}`,
}));

/**
 * The emblem at the head of a page or a panel: notifications, the chat room.
 *
 * `Emblem` is the mascot's night navy in both color schemes, which is right
 * for a small mark on a tinted card and a dark block at the top of a white
 * page. This one follows the scheme: a pale blue tile with a deep blue icon in
 * the light one, a tile of blue light with a sky icon in the dark one.
 */
export const HeadingEmblem = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 44,
  height: 44,
  display: "grid",
  placeItems: "center",
  borderRadius: 13,
  fontSize: 24,
  color: MASCOT_PALETTE.deepInk,
  backgroundColor: alpha(colors.prime, 0.12),
  boxShadow: `inset 0 0 0 1px ${alpha(colors.prime, 0.28)}`,
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    backgroundColor: alpha(MASCOT_PALETTE.sky, 0.12),
    boxShadow: `inset 0 0 0 1px ${alpha(MASCOT_PALETTE.sky, 0.32)}`,
  }),
}));
